import { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import Register from "./pages/Register/Register";
// import Dashboard from "./pages/Dashboard/Dashboard";
import SideBar from "./shared/SideBar/SideBar";
import { RoutesReact } from "./constants/routes";
import ConfirmedGym from "./pages/ConfirmedGym/ConfirmedGym";
import GlobalLoadingPage from "./shared/GlobalLoadingPage/GlobalLoadingPage";
import AthleteLogin from "./pages/AthleteLogin/AthleteLogin";
import { TeamsAndSpaces } from "./pages/TeamsAndSpaces/TeamsAndSpaces";
import SpacePage from "./pages/Space/SpacePage";
import UsersPage from "./pages/UsersPage/UsersPage";
import CalendarPage from "./pages/CalendarPage/CalendarPage";
import LoadingPage from "./shared/LoadingPage/LoadingPage";
import { useCustomKeycloak } from "./hooks/useCustomKeycloak";
import { SelectedEntityGuard } from "./Guards/SelectedEntityGuard";
import { ConfirmTokenPage } from "./pages/ConfirmTokenPage/ConfirmTokenPage";
import ConfirmAthlete from "./pages/ConfirmAthlete/ConfirmAthlete";
import ConfirmDirector from "./pages/ConfirmDirector/ConfirmDirector";
import ConfirmUser from "./pages/ConfirmUser/ConfirmUser";
import ConfirmUserGym from "./pages/ConfirmUserGym/ConfirmUserGym";
import NotificationsPage from "./pages/NotificationsPage/NotificationsPage";
import { ConfirmModal } from "./shared/ConfirmModal/ConfirmModal";
import { ProfilePage } from "./pages/ProfilePage/ProfilePage";
import { SupportoPage } from "./pages/SupportoPage/SupportoPage";
import { DetailTeamPage } from "./pages/DetailTeamPage/DetailTeamPage";
import SubjectReadinessPage from "./pages/SubjectReadinessPage/SubjectReadinessPage";
import UserDetailPage from "./pages/UserDetailPage/UserDetailPage";
import TestPage from "./pages/TestPage/TestPage";
import Body3DTest from "./pages/Body3DTest/Body3DTest";
import ProgrammingPage from "./pages/ProgrammingPage/ProgrammingPage";
import { CarichiPage } from "./pages/CarichiPage/CarichiPage";
import { ProfilingHubPage } from "./pages/ProfilingHubPage/ProfilingHubPage";
import { ProfilingProtocolDetailPage } from "./pages/ProfilingProtocolDetailPage/ProfilingProtocolDetailPage";
import { AthleteExerciseHistoryPage } from "./pages/AthleteExerciseHistoryPage/AthleteExerciseHistoryPage";
import MatchPage from "./pages/MatchPage/MatchPage";
import CoachSessionPostPage from "./pages/CoachSessionPostPage/CoachSessionPostPage";
import CoachSessionWeekView from "./pages/CoachSessionWeekView/CoachSessionWeekView";
import CoachSessionDetailPage from "./pages/CoachSessionDetail/CoachSessionDetailPage";
import CoachSessionViewPage from "./pages/CoachSessionView/CoachSessionViewPage";
import TrainingStatisticsDashboard from "./pages/TrainingStatisticsDashboard/TrainingStatisticsDashboard";
import WeeklyReportsPage from "./pages/WeeklyReports/WeeklyReportsPage";
import MicrocycleTemplatesPage from "./pages/MicrocycleTemplates/MicrocycleTemplatesPage";
import { AiCoachPage } from "./pages/AiCoachPage/AiCoachPage";
import AbbonamentoPage from "./pages/AbbonamentoPage/AbbonamentoPage";
import AllenamentiPage from "./pages/AllenamentiPage/AllenamentiPage";
import Login from "./pages/Login/Login";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import "./App.scss";
import { InfoDrawer } from "./components/InfoDrawer/InfoDrawer";
import { CookieBanner } from "./shared/CookieBanner/CookieBanner";
import { PrivacyConsentModal } from "./shared/PrivacyConsentModal/PrivacyConsentModal";
import JoinTeamPage from "./pages/JoinTeamPage/JoinTeamPage";
import { PrivacyPage } from "./pages/Privacy/PrivacyPage";
import { TermsPage } from "./pages/Privacy/TermsPage";
import { ModuleVisibilityProvider } from "./providers/ModuleVisibilityProvider";
import { ModuleVisibilityGuard } from "./Guards/ModuleVisibilityGuard";

// const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));

const App = () => {
  const { initialized } = useCustomKeycloak();

  if (!initialized) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center">
        <LoadingPage />
      </div>
    );
  }

  return (
    <>
      <InfoDrawer />
      <CookieBanner />
      <PrivacyConsentModal />
      <Suspense fallback={<div>loading</div>}>
        <Routes>
          <Route path={RoutesReact.DIRECTOR_LOGIN} element={<Login />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {process.env.NODE_ENV !== "production" && (
            <Route path="/test" element={<TestPage />} />
          )}
          {process.env.NODE_ENV !== "production" && (
            <Route path="/body-3d-test" element={<Body3DTest />} />
          )}
          <Route path={RoutesReact.ATHLETE_LOGIN} element={<AthleteLogin />} />
          <Route path="/confirm-token" element={<ConfirmTokenPage />} />
          {/* Link di invito condiviso dal coach: pubblico, senza entità selezionata */}
          <Route path="/join/:code" element={<JoinTeamPage />} />
          {/* BUG-AUDIT-002 fix: wired routes per token confirm flow */}
          <Route path="/confirm-athlete/:token" element={<ConfirmAthlete />} />
          <Route path="/confirm-director/:token" element={<ConfirmDirector />} />
          <Route path="/confirm-user/:token" element={<ConfirmUser />} />
          <Route path="/confirm-user-gym/:token" element={<ConfirmUserGym />} />
          <Route path={RoutesReact.GYM_REGISTER} element={<Register />} />
          <Route path={RoutesReact.GYM_CONFIRMED} element={<ConfirmedGym />} />

          <Route
            path={RoutesReact.DIRECTOR_DASHBOARD}
            element={
              <SelectedEntityGuard>
                <ModuleVisibilityProvider>
                  <SideBar />
                </ModuleVisibilityProvider>
              </SelectedEntityGuard>
            }
          >
            <Route path="" element={<Navigate to={"spaces"} />}></Route>
            {/* Ogni route è wrappata in ModuleVisibilityGuard (feature-flag runtime):
                pageKey/moduleKey da module-visibility/seed-default.json. "profile" resta
                senza guard: è il fallback di redirect, mai nascondibile. */}
            <Route path="users" element={<ModuleVisibilityGuard pageKey="dashboard-users" moduleKey="users"><UsersPage /></ModuleVisibilityGuard>}></Route>
            <Route path="users/:id" element={<ModuleVisibilityGuard pageKey="dashboard-user-detail" moduleKey="users"><UserDetailPage /></ModuleVisibilityGuard>}></Route>
            <Route path="calendar" element={<ModuleVisibilityGuard pageKey="dashboard-calendar" moduleKey="calendar"><CalendarPage /></ModuleVisibilityGuard>}></Route>
            <Route path="notifications" element={<ModuleVisibilityGuard pageKey="dashboard-notifications" moduleKey="notifications"><NotificationsPage /></ModuleVisibilityGuard>}></Route>
            <Route path="spaces" element={<ModuleVisibilityGuard pageKey="dashboard-spaces" moduleKey="teams-and-spaces"><TeamsAndSpaces /></ModuleVisibilityGuard>} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="supporto" element={<ModuleVisibilityGuard pageKey="dashboard-supporto"><SupportoPage /></ModuleVisibilityGuard>} />
            <Route path="spaces/:id" element={<ModuleVisibilityGuard pageKey="dashboard-spaces-detail" moduleKey="teams-and-spaces"><SpacePage /></ModuleVisibilityGuard>} />
            <Route path="spaces/:spaceId/teams/:id" element={<ModuleVisibilityGuard pageKey="dashboard-team-detail" moduleKey="teams-and-spaces"><DetailTeamPage /></ModuleVisibilityGuard>} />
            <Route
              path="subject-readiness"
              element={<ModuleVisibilityGuard pageKey="dashboard-subject-readiness" moduleKey="readiness"><SubjectReadinessPage /></ModuleVisibilityGuard>}
            />
            <Route path="programming" element={<ModuleVisibilityGuard pageKey="dashboard-programming" moduleKey="programming"><ProgrammingPage /></ModuleVisibilityGuard>} />
            <Route path="carichi" element={<ModuleVisibilityGuard pageKey="dashboard-carichi" moduleKey="carichi"><CarichiPage /></ModuleVisibilityGuard>} />
            {/* Modulo Profiling — M4 */}
            <Route path="users/:athleteId/profiling" element={<ModuleVisibilityGuard pageKey="dashboard-profiling-hub" moduleKey="users"><ProfilingHubPage /></ModuleVisibilityGuard>} />
            <Route path="users/:athleteId/profiling/:protocolId" element={<ModuleVisibilityGuard pageKey="dashboard-profiling-protocol-detail" moduleKey="users"><ProfilingProtocolDetailPage /></ModuleVisibilityGuard>} />
            <Route path="users/:athleteId/exercises/:exerciseId" element={<ModuleVisibilityGuard pageKey="dashboard-athlete-exercise-history" moduleKey="users"><AthleteExerciseHistoryPage /></ModuleVisibilityGuard>} />
            {/* Coach Session Designer — Fase 3 (UI Session Builder) */}
            <Route path="teams/:teamId/sessions" element={<ModuleVisibilityGuard pageKey="dashboard-coach-sessions-week" moduleKey="calendar"><CoachSessionWeekView /></ModuleVisibilityGuard>} />
            {/* a48bb7 — vista sola lettura della sessione (il click apre questa, "Modifica" → editor) */}
            <Route path="teams/:teamId/sessions/:sessionId/view" element={<ModuleVisibilityGuard pageKey="dashboard-coach-session-view" moduleKey="calendar"><CoachSessionViewPage /></ModuleVisibilityGuard>} />
            <Route path="teams/:teamId/sessions/:sessionId/edit" element={<ModuleVisibilityGuard pageKey="dashboard-coach-session-edit" moduleKey="calendar"><CoachSessionDetailPage /></ModuleVisibilityGuard>} />
            {/* Bug 6a01ee9a — Sessioni Campo Individuali (athleteIds[] senza teamId) */}
            <Route path="coach-sessions/:sessionId/edit" element={<ModuleVisibilityGuard pageKey="dashboard-coach-session-edit" moduleKey="calendar"><CoachSessionDetailPage /></ModuleVisibilityGuard>} />
            {/* Coach Session Designer — Fase 4 */}
            <Route path="teams/:teamId/matches/:matchId" element={<ModuleVisibilityGuard pageKey="dashboard-match" moduleKey="calendar"><MatchPage /></ModuleVisibilityGuard>} />
            <Route path="teams/:teamId/sessions/:sessionId/post" element={<ModuleVisibilityGuard pageKey="dashboard-coach-session-post" moduleKey="calendar"><CoachSessionPostPage /></ModuleVisibilityGuard>} />
            {/* Fase 8 — Dashboard Statistiche Allenamento */}
            <Route path="teams/:teamId/training-stats" element={<ModuleVisibilityGuard pageKey="dashboard-training-stats" moduleKey="calendar"><TrainingStatisticsDashboard /></ModuleVisibilityGuard>} />
            {/* Modulo Allenamenti — pianificazione settimanale a blocchi */}
            <Route path="teams/:teamId/allenamenti" element={<ModuleVisibilityGuard pageKey="dashboard-allenamenti" moduleKey="calendar"><AllenamentiPage /></ModuleVisibilityGuard>} />
            {/* Fase 7 — Weekly Reports + Microcycle Templates */}
            <Route path="teams/:teamId/weekly-reports" element={<ModuleVisibilityGuard pageKey="dashboard-weekly-reports" moduleKey="calendar"><WeeklyReportsPage /></ModuleVisibilityGuard>} />
            <Route path="teams/:teamId/microcycle-templates" element={<ModuleVisibilityGuard pageKey="dashboard-microcycle-templates" moduleKey="calendar"><MicrocycleTemplatesPage /></ModuleVisibilityGuard>} />
            {/* AI Coach — Q&A naturale + briefing automatico */}
            <Route path="ai-coach" element={<ModuleVisibilityGuard pageKey="dashboard-ai-coach" moduleKey="ai-coach"><AiCoachPage /></ModuleVisibilityGuard>} />
            {/* Abbonamento — read-only piano corrente */}
            <Route path="profile/abbonamento" element={<ModuleVisibilityGuard pageKey="dashboard-profile-abbonamento"><AbbonamentoPage /></ModuleVisibilityGuard>} />
          </Route>

          <Route
            path="*"
            element={<Navigate to={RoutesReact.DIRECTOR_LOGIN} />}
          ></Route>
        </Routes>
      </Suspense>
      <GlobalLoadingPage />
      <ConfirmModal />
    </>
  );
};

export default App;
